import { useEffect, useState } from "react";
import { Check, LoaderCircle, Settings2 } from "lucide-react";
import { Button } from "@shared/components/ui/button";
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@shared/components/ui/card";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";

type View = "loading" | "settings";

/**
 * Suggested models: the analysis on the most capable one, the rest on the fast one.
 * Claude goes through Bedrock under the EU inference profile, the only one
 * served on demand for recent models.
 */
const SUGGESTED_MODELS = [
  "deepseek-flash",
  "deepseek-v4-pro",
  "eu.anthropic.claude-sonnet-5-5",
  "eu.anthropic.claude-opus-5-5",
  "eu.anthropic.claude-haiku-5-5",
];

async function fetchSettings() {
  const response = await fetch("/api/admin/model", { cache: "no-store" });
  if (response.status === 401) return null;

  const data = (await response.json()) as { error?: string; model?: string; email?: string };
  if (!response.ok || !data.model) throw new Error(data.error || "Configuration indisponible.");
  return { model: data.model, email: data.email || "" };
}

export function AdminSettings() {
  const [view, setView] = useState<View>("loading");
  const [email, setEmail] = useState("");
  const [model, setModel] = useState("");
  const [savedModel, setSavedModel] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    let active = true;
    fetchSettings()
      .then((settings) => {
        if (!active) return;
        if (!settings) throw new Error("Session administrateur expirée.");
        setModel(settings.model);
        setSavedModel(settings.model);
        setEmail(settings.email);
        setView("settings");
      })
      .catch((error) => {
        if (!active) return;
        setMessage(error instanceof Error ? error.message : "Configuration indisponible.");
        setView("settings");
      });
    return () => {
      active = false;
    };
  }, []);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const response = await fetch("/api/admin/model", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Mise à jour refusée.");
      setModel(data.model);
      setSavedModel(data.model);
      setMessage("Modèle enregistré. Les prochaines analyses utiliseront ce réglage.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Mise à jour refusée.");
    } finally {
      setBusy(false);
    }
  }

  if (view === "loading") {
    return (
      <Card className="items-center justify-center py-8">
        <p className="m-0 flex items-center gap-2.5 text-[13px] text-muted-foreground">
          <LoaderCircle className="size-4 animate-spin" /> Vérification de la session…
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3.5 border-b pb-4">
        <CardTitle className="inline-flex items-center gap-2 text-[13px] font-semibold">
          <Settings2 className="size-4 text-muted-foreground" /> Modèle de l’agent
        </CardTitle>
        {email && <CardAction><span className="text-[12px] text-muted-foreground">Session : {email}</span></CardAction>}
      </CardHeader>
      <CardContent>
        <form className="grid max-w-[520px] gap-3" onSubmit={save}>
          <div className="grid gap-2">
            <Label htmlFor="agent-model">Identifiant du modèle</Label>
            <Input
              id="agent-model"
              onChange={(event) => setModel(event.target.value)}
              placeholder="deepseek-flash"
              required
              spellCheck={false}
              value={model}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {SUGGESTED_MODELS.map((suggestion) => (
              <Button key={suggestion} onClick={() => setModel(suggestion)} size="sm" type="button" variant="outline">
                {suggestion}
              </Button>
            ))}
          </div>
          <small className="text-[12px] leading-relaxed text-muted-foreground">
            Lu en base au démarrage de chaque analyse. Un modèle <code>deepseek-*</code> passe par DeepSeek
            (<code>DEEPSEEK_API_KEY</code>) ; un modèle <code>anthropic.*</code> ou <code>eu.anthropic.*</code> par
            Amazon Bedrock (<code>AWS_REGION</code> et <code>AWS_BEARER_TOKEN_BEDROCK</code>, ou une paire de clés IAM) ;
            tout autre identifiant passe par OpenAI (<code>OPENAI_API_KEY</code>), en repli. Le modèle est vérifié auprès
            du fournisseur avant l’enregistrement (pour Bedrock, par une requête minuscule). L’enrichissement utilise{" "}
            <code>deepseek-flash</code> (variable <code>ENRICHMENT_MODEL</code>, qui accepte aussi un modèle Bedrock).
          </small>
          <Button className="w-fit" disabled={busy || model.trim() === savedModel} type="submit">
            {busy ? <LoaderCircle className="animate-spin" /> : <Check />} Enregistrer
          </Button>
        </form>
        {message && <p className="mt-4 mb-0 text-[12.5px] text-muted-foreground">{message}</p>}
      </CardContent>
    </Card>
  );
}
